import { IngestionSourceOption, NormalizedIngestionSource } from '../types'
import { stringOrNumberSort, toCamel } from '../../../components/Helpers'
import {
  getS3File,
  getS3FolderContents,
} from '../../../components/ingestion/Utility'
import { doInBatches } from '../../../utility'
import {
  breadthFirstTraversal,
  breadthFirstTraversalAsync,
} from '../../../components/utility/TreeHelpers'
import {
  getDrivePdfFile,
  getFilesRecursive2,
  GoogleUserAtomValue,
  GDriveFile,
} from 'infrastructure/googleDrive'
import { GDriveContent } from './types'
import { extractPdfLinksFromBlob } from 'shared/utils/pdfFile'

type parseContext = {
  doc: Document
}

export async function initialTransform(
  source: IngestionSourceOption,
  cred: GoogleUserAtomValue | undefined,
  setUserMessage: (m: string) => void,
) {
  setUserMessage('Retrieving Google Drive Files...')
  const stuff = await googleFiles(cred)
  setUserMessage('Parsing source files...')
  const children = await Promise.all(
    source.sourceFiles.map(async ({ URL }) => {
      const file = await fetch(URL)
      const html = await file.text()
      const domParser = new DOMParser()
      const doc = domParser.parseFromString(html, 'text/html')
      const scriptTag = doc.querySelector('script[type="application/ld+json"]')
      if (!scriptTag) return null
      const rawJson = JSON.parse(scriptTag.textContent || '{}')
      return parseNode(rawJson, { doc }, undefined)
    }),
  )

  const grade = String(children[0]!.grade)
  const normalizedCourse = {
    children,
    type: 'kid:Course',
    grade,
    subjectId: children[0]!.subjectId,
    title: `Odell Education: Grade ${children[0]!.grade} High School Literacy Program`,
    'kid:programGuidance': {
      programGuide: gFileToParams(stuff?.programGuide),
      remoteLearningGuide: gFileToParams(stuff?.remoteLearningGuide),
    },
  } as NormalizedIngestionSource
  setUserMessage('Parsing GDrive files...')
  stuff && (await doGdriveFiles(normalizedCourse, stuff, grade, cred))
  setUserMessage('Retrieving material files...')
  const materials = await getMaterials(normalizedCourse)
  await doAllMaterials(normalizedCourse, materials)
  setUserMessage('')
  return normalizedCourse
}

async function getMaterials(source: NormalizedIngestionSource) {
  const sourceCodes = source.children!.map((c) => c.courseCode)
  return (
    await Promise.all(
      sourceCodes.map((c) => {
        return getS3FolderContents(
          `partners/Odell/export/${String(c).toLowerCase()}/`,
        )
      }),
    )
  ).flat()
}

function parseNode(
  node: any,
  context: parseContext,
  parentNode: any | undefined,
): NormalizedIngestionSource {
  const ignoredKeys = [
    '@context',
    'alignment', // These seem to be all empty of content
    'classConfiguration', // These seem to be all empty of content
    'isPartOf',
  ]
  const identityKeys = [
    'about',
    'alternateName',
    'courseCode',
    'description',
    'readingPurpose',
    'activityType',
    'aboutTheActivityTeacher',
    'learningResourceType',
    'when',
    'name',
    'url',
    'readingKnowledgeGoals',
    'writingGoals',
    'question',
  ]

  return Object.entries(node).reduce<NormalizedIngestionSource>(
    (acc, [key, value]: [string, any]) => {
      if (!value) return acc
      switch (key) {
        case '@type': {
          const type = getTypeForSource(value)
          const extraStuff = extraStuffForType(type, node, parentNode)
          return {
            ...acc,
            type,
            ...extraStuff,
          }
        }
        case '@id': {
          return {
            ...acc,
            id: value,
            ...getHtmlPartsForId(
              context.doc,
              String(value),
              parentNode?.['@id'],
            ),
          }
        }
        case 'hasPart': {
          if (!Array.isArray(value)) return acc
          return {
            ...acc,
            children: value.map((n: Record<string, unknown>) =>
              parseNode(n, context, node),
            ),
          }
        }
        case 'educationalAlignment': {
          if (value.alignmentType === 'Educational level') {
            return {
              ...acc,
              grade: Number(value.targetName),
            }
          }
          return {
            ...acc,
            standards: doStandardsForSource(node),
          }
        }
        case 'forCourse': {
          if (value.courseIdentifier === 'ELA') {
            return {
              ...acc,
              subjectId: 4481,
            }
          }
          throw new Error(
            `Unrecognized courseIdentifier: ${value.courseIdentifier}`,
          )
        }
        case 'material': {
          return {
            ...acc,
            material: doMaterials(value),
          }
        }
        case 'focus': {
          return {
            ...acc,
            focus: value.name,
          }
        }
        case 'lessonGoals': {
          return {
            ...acc,
            lessonGoals: value.mainContent,
          }
        }
        case 'sectionGoals': {
          return {
            ...acc,
            sectionGoals: value.mainContent,
          }
        }
        default:
          if (ignoredKeys.includes(key)) {
            return acc
          }
          if (identityKeys.includes(key) || typeof value === 'string') {
            return {
              ...acc,
              [key]: value,
            }
          }
          throw new Error(`Unrecognized source key: ${key}`)
      }
    },
    {} as NormalizedIngestionSource,
  )
}

function getTypeForSource(typeVal: unknown) {
  return Array.isArray(typeVal) ? typeVal[0] : String(typeVal)
}

function getHtmlPartsForId(
  doc: Document,
  currentId: string,
  parentId?: string,
) {
  const extraContent = ['Culminating-Task', 'Section-Diagnostic']
  const htmlParts = extraContent.reduce<
    Record<string, string | { student?: string; teacher?: string }[]>
  >((acc, partId) => {
    const anchorId = `${currentId}-${partId}`
    const htmlContent = doc.getElementById(anchorId)
    if (htmlContent) {
      acc[toCamel(partId.toLowerCase())] = htmlContent.outerHTML
    }
    return acc
  }, {})
  if (currentId.includes('Activity_')) {
    const segments: { student?: string; teacher?: string }[] = []
    const anchorId = currentId.split('#').pop()!
    const parent = parentId ? (doc.getElementById(parentId) ?? doc) : doc
    const activityEl = parent.querySelector(`#${anchorId}`)
    const segElements = activityEl?.querySelectorAll('[data-id][data-type]')

    segElements?.forEach((seg) => {
      const segId = seg.getAttribute('data-id')
      const segType = seg.getAttribute('data-type')
      if (!segId || !segType) return
      const segIndex = Number(segId) - 1
      if (!segments[segIndex]) segments[segIndex] = {}
      if (segType === 'segment-student')
        segments[segIndex].student = seg.outerHTML
      if (segType === 'segment-teacher')
        segments[segIndex].teacher = seg.outerHTML
    })
    if (Object.keys(segments).length) {
      htmlParts.segments = segments
    }
  }
  return htmlParts
}

function doMaterials(materials: Record<string, unknown>[]) {
  return materials.map((m) => {
    return Object.entries(m).reduce<Record<string, unknown>>(
      (acc, [key, val]) => {
        const cleanKey = key.startsWith('@') ? key.slice(1) : key
        const cleanVal = key === 'name' ? String(val).normalize('NFC') : val
        return {
          ...acc,
          [cleanKey]: cleanVal,
        }
      },
      {},
    )
  })
}

async function doGdriveFiles(
  source: NormalizedIngestionSource,
  gDrive: GDriveContent,
  grade: string,
  cred: GoogleUserAtomValue | undefined,
) {
  const { studentEditions, teacherEditions } = gDrive
  const studentFiles = studentEditions?.[grade] || []
  const teacherFiles = teacherEditions?.[grade] || []
  await breadthFirstTraversalAsync(source, 'children', async (node) => {
    if (node.type !== 'oer:Module') return
    const name = `${grade}_${String(node.alternateName)}`.toLowerCase()
    const teacher = teacherFiles.find((f) =>
      f.name.toLowerCase().includes(name),
    )
    const fileData: Record<
      string,
      { url?: string; title: string } | undefined
    > = {
      teacher: { ...gFileToParams(teacher), title: 'Teacher Edition' },
      student: {
        ...gFileToParams(
          studentFiles.find((f) => f.name.toLowerCase().includes(name)),
        ),
        title: 'Student Materials',
      },
    }
    node['kid:gDrive'] = fileData

    const linkTexts = [
      'Evaluation Plan',
      'Culminating Task Checklist',
      'Text Overview',
      'Unit Text List',
    ]
    const regex = new RegExp(linkTexts.join('|'), 'i')
    if (teacher?.id && cred) {
      const { file } = await getDrivePdfFile(cred, teacher.id)
      if (!file) return
      const links = await extractPdfLinksFromBlob(file, regex)
      linkTexts.forEach((text) => {
        const file = links.find(({ label }) => label.includes(text))
        fileData[toCamel(text)] = { title: text, url: file?.url }
      })
    }
  })
}

async function doAllMaterials(
  source: NormalizedIngestionSource,
  sourceMaterials: { key: string }[],
) {
  const moduleChildren = source.children!.filter(
    ({ type }) => type === 'oer:Module',
  )
  await Promise.all(
    moduleChildren.map((m) => doAllMaterialsForUnit(m, sourceMaterials)),
  )
}

async function doAllMaterialsForUnit(
  source: NormalizedIngestionSource,
  sourceMaterials: { key: string }[],
) {
  const courseCode = String(source.courseCode).toLowerCase()
  const materials: Record<string, { html?: string; pdfUrl?: string }> = {}

  breadthFirstTraversal(source, 'children', (current) => {
    const material = current.material
    if (!Array.isArray(material)) return
    material.forEach((m) => {
      materials[m.name] = {
        ...m,
      }
    })
  })

  const doKey = async (key: string) => {
    const htmlTest = `${courseCode}/${key}.html`.normalize('NFC')
    const htmlFile = sourceMaterials.find((f) => {
      const testAgainst = f.key.normalize('NFC')
      return (
        testAgainst.includes(htmlTest) ||
        testAgainst.includes(htmlTest.replaceAll(':', ''))
      )
    })
    if (!htmlFile) return
    const resp = await getS3File(htmlFile)
    materials[key].html = await resp.text()

    const pdfTest = `${courseCode}/${key}.pdf`.normalize('NFC')
    const pdfFile = sourceMaterials.find((f) => {
      const testAgainst = f.key.normalize('NFC')
      return (
        testAgainst.includes(pdfTest) ||
        testAgainst.includes(pdfTest.replaceAll(':', ''))
      )
    })
    if (!pdfFile) return
    materials[key].pdfUrl =
      `https://kiddom-ingestion.s3.us-east-2.amazonaws.com/${pdfFile.key}`
  }

  await doInBatches(Object.keys(materials), doKey)

  breadthFirstTraversal(source, 'children', (current) => {
    const material = current.material
    if (!Array.isArray(material)) return
    material?.forEach((m) => {
      m.html = materials[m.name]?.html
      m.pdfUrl = materials[m.name]?.pdfUrl
    })
  })
}

function doStandardsForSource(source: any) {
  const value = source.educationalAlignment
  if (!Array.isArray(value)) return []
  return value.reduce<string[]>((acc, { alignmentType, targetName }) => {
    if (alignmentType === 'educationalSubject') {
      if (!acc.includes(targetName)) acc.push(targetName)
    } else if (alignmentType !== 'Educational level') {
      throw new Error(`educationalAlignment Unrecognized: ${alignmentType}`)
    }
    return acc
  }, [])
}

function extraStuffForType(type: string, sourceNode: any, parentNode: any) {
  if (
    type === 'oer:Activity' &&
    (parentNode?.lessonGoals as Record<string, string>)?.mainContent
  ) {
    // This is a special case where the lessonGoals are in the parent node
    // and we need to copy them over to the activity node to make them
    // available at the Activity level
    return {
      lessonGoals: String(
        (parentNode?.lessonGoals as Record<string, string>)?.mainContent,
      ),
    }
  }

  if (type === 'oer:Unit') {
    const foundNames: string[] = []

    const filteredMaterials: any[] = []
    const standards: string[] = []

    // In this case we need to pull activity materials up to the unit level
    // Also pulling any standards that are associated with the unit
    breadthFirstTraversal(sourceNode, 'hasPart', (current) => {
      doStandardsForSource(current).forEach((s: string) => {
        if (standards.includes(s)) return
        standards.push(s)
      })

      const currentType = getTypeForSource(current['@type'])
      if (currentType !== 'oer:Activity') return

      const material = current.material
      if (!Array.isArray(material)) return

      filteredMaterials.push(
        ...material.filter((m) => {
          if (foundNames.includes(m.name.trim())) return false
          foundNames.push(m.name.trim())
          return m['@type'] === 'ocx:AssociatedMaterial'
        }),
      )
    })
    return {
      'kid:actMat': doMaterials(filteredMaterials),
      standards: standards.sort(stringOrNumberSort),
    }
  }

  return {}
}

async function googleFiles(cred: GoogleUserAtomValue | undefined) {
  const files =
    cred?.credential?.accessToken &&
    (await getFilesRecursive2(
      cred.credential.accessToken,
      '1JXkW7knXyfohKJtqwMA4AZsBvlHhfCZ_',
    ))
  if (!Array.isArray(files)) return
  const gradeRegex = /G(\d{1,2})/
  return files.reduce<GDriveContent>((acc, file) => {
    if (file.name.includes('Remote Learning Guide')) {
      acc.remoteLearningGuide = file
    } else if (file.name.includes('Program Guide')) {
      acc.programGuide = file
    } else if (
      file.namePath.startsWith('/TEACHER EDITIONS/PRINT READY PDFS/')
    ) {
      acc.teacherEditions = acc.teacherEditions || {}
      const match = gradeRegex.exec(file.name)
      if (!match) {
        return acc
      }
      acc.teacherEditions[match[1]] ||= []
      acc.teacherEditions[match[1]].push(file)
    } else if (
      file.namePath.startsWith('/STUDENT MATERIALS/PRINT READY PDFS/')
    ) {
      acc.studentEditions = acc.studentEditions || {}
      const match = gradeRegex.exec(file.name)
      if (!match) {
        return acc
      }
      acc.studentEditions[match[1]] ||= []
      acc.studentEditions[match[1]].push(file)
    }
    return acc
  }, {})
}

function gFileToParams(file?: GDriveFile) {
  if (!file) return
  const { name, id } = file
  const match = /_([^_]*\s[^_]*)_/.exec(name)
  const title = match?.[1] ?? name.split('.')[0]
  return {
    title,
    url: `https://drive.google.com/file/d/${id}/view`,
  }
}
